import React from 'react';

import qs from 'qs';
import { FormattedMessage, InjectedIntl, injectIntl } from 'react-intl';

import Button from '@atlaskit/button';
import ButtonGroup from '@atlaskit/button/button-group';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import DropdownMenu, {
  DropdownItemGroup,
  DropdownItem,
} from '@atlaskit/dropdown-menu';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { ModalTransition } from '@atlaskit/modal-dialog';
import PageHeader from '@atlaskit/page-header';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { Extension } from '@atlassian/forge-ui';

import { Repository } from 'src/components/types';
import { ConnectFileActions } from 'src/connect/file-modules';
import {
  LazyForgeRepoCodeOverviewActionAppsList,
  preloadLoadableDropdownAppList,
} from 'src/forge/repo-code-overview-action/lazy-repo-code-overview-action-apps-list';
import { LazyForgeRepoCodeOverviewActionSingleApp } from 'src/forge/repo-code-overview-action/lazy-repo-code-overview-action-single-app';
import { RouteComponentProps } from 'src/router/types';
import {
  PageHeaderColumn,
  PageHeaderWrapper,
} from 'src/sections/global/components/page.style';
import DownloadRepositoryLfsModalDialog from 'src/sections/repository/components/download-repository-lfs-modal-dialog';
import SubscriptionsModalDialog from 'src/sections/repository/containers/subscriptions-modal-dialog';
import repoUrls from 'src/sections/repository/urls';
import urls from 'src/urls/source';
import { publishUiEvent } from 'src/utils/analytics/publish';
import authRequest from 'src/utils/fetch';

import RepositoryAccessGuard from '../../../components/repository-access-guard';
import Breadcrumbs from '../../../containers/breadcrumbs';
import CheckoutDialogButton from '../../../containers/checkout-button';
import CloneButton from '../../../containers/clone-button';
import { Ref, RepositoryPrivilege } from '../../../types';
import { TreeEntryType } from '../types';

import messages from './header.i18n';

type Props = RouteComponentProps & {
  currentRef: Ref | null | undefined;
  hash?: string;
  intl: InjectedIntl;
  path: string | null | undefined;
  pathType: TreeEntryType | null | undefined;
  repository?: Repository;
  forkPolicy?: string;
  repositoryAccessLevel?: RepositoryPrivilege;
  userIsAnonymous: boolean;
};

type State = {
  isDownloadRepoModalOpen: boolean;
  openedExtension?: Extension;
};

class Header extends React.Component<Props, State> {
  state = {
    isDownloadRepoModalOpen: false,
    openedExtension: undefined,
  };

  componentDidMount() {
    // preload Forge JS chunk to prevent dropdown menu from blinking
    preloadLoadableDropdownAppList();
  }

  onOpenSubscriptionsDialog = () => {
    const { pathname } = this.props.location;
    this.props.history.push(`${pathname}?subscriptionsModalOpened=true`);
  };

  onCloseSubscriptionsDialog = () => {
    const { pathname } = this.props.location;
    this.props.history.push(pathname);
  };

  closeDownloadRepoModal = () => {
    this.setState({ isDownloadRepoModalOpen: false });
  };

  openDownloadRepoModal = () => {
    this.setState({ isDownloadRepoModalOpen: true });
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  getCreateFilePath = (path, pathType) => {
    if (!path) {
      return null;
    }
    if (pathType === 'directory') {
      return path && `${path}/`;
    }
    // Assume it's a file if it's not a directory.
    // We need the directory path where the file is, so take
    // the path up to the last path separator. Note that we
    // need to include the slash, so +1 to the last index
    return path && path.substr(0, path.lastIndexOf('/') + 1);
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  shareRepo = () => {
    const { repository } = this.props;
    if (!repository) {
      return;
    }
    // XXX: Temporary workaround until BBCDEV-8501 is implemented
    window.location.href = urls.ui.permissions(repository.full_name);
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  onDownloadRepoAction = async repository => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'dropdownItem',
      actionSubjectId: 'sourceHeaderDropdownItem',
      source: 'repositorySourceScreen',
      attributes: {
        item: 'downloadRepository',
      },
    });

    try {
      const getRepositoryMetadataResponse = await fetch(
        authRequest(
          urls.api.internal.repositoryMetadataURL(repository.full_name)
        )
      );

      const { has_lfs_files: hasLfsFiles } =
        await getRepositoryMetadataResponse.json();

      if (hasLfsFiles) {
        this.openDownloadRepoModal();
      } else {
        this.downloadRepo();
      }
    } catch {
      this.downloadRepo();
    }
  };

  // eslint-disable-next-line consistent-return
  downloadRepo = () => {
    const { repository, currentRef } = this.props;
    if (!repository || !currentRef) {
      return '';
    }
    window.location.href = urls.api.v20.downloadRepo(
      repository.full_name,
      currentRef.target?.hash
    );
  };

  onAppClick = (app: Extension) => this.setState({ openedExtension: app });

  onAppClose = () => this.setState({ openedExtension: undefined });

  renderActions() {
    const {
      currentRef,
      hash,
      intl,
      path,
      pathType,
      repository,
      forkPolicy,
      repositoryAccessLevel,
      userIsAnonymous,
    } = this.props;

    if (!repository || !hash) {
      return undefined;
    }
    const [owner, slug] = repository.full_name.split('/');

    const frontbucketRepoSourceAddFileEnabled = FeatureGates.checkGate(
      StatsigFeatureKeys.frontbucketRepoSourceAddFile
    );

    const isBranchCompareEnabled = FeatureGates.checkGate(
      StatsigFeatureKeys.frontbucketBranchCompare
    );

    const createFileUrl = urls.ui.createFile(repository.full_name, {
      refOrHash: hash,
      path: this.getCreateFilePath(path, pathType),
      at: currentRef ? currentRef.name : undefined,
    });

    const HeaderDropdownMenu = () => (
      // Temp inline style override AK bug: https://ecosystem.atlassian.net/projects/AK/queues/issue/AK-5837
      <div style={{ textAlign: 'left' }}>
        <DropdownMenu
          aria-label={intl.formatMessage(messages.actionsMenu)}
          placement="bottom-end"
          spacing="compact"
          trigger={({ triggerRef, ...props }) => (
            <Button
              {...props}
              iconBefore={
                <MoreIcon
                  label={intl.formatMessage(messages.actionsMenu)}
                  size="medium"
                />
              }
              ref={triggerRef}
            />
          )}
          testId="repo-actions-menu"
        >
          <DropdownItemGroup>
            <RepositoryAccessGuard requiredLevel="read">
              {(forkPolicy === 'allow_forks' ||
                forkPolicy === 'no_public_forks') && (
                <DropdownItem
                  data-testid="repo-menu-create-fork"
                  href={repoUrls.ui.fork(owner, slug)}
                >
                  <FormattedMessage {...messages.forkRepoAction} />
                </DropdownItem>
              )}
              <DropdownItem
                href={repoUrls.ui.compare(owner, slug)}
                onClick={e => {
                  if (isBranchCompareEnabled) {
                    e.preventDefault();
                    this.props.history.push(repoUrls.ui.compare(owner, slug));
                  }

                  publishUiEvent({
                    action: 'clicked',
                    actionSubject: 'dropdownItem',
                    actionSubjectId: 'compareBranchesDropdownItem',
                    source: 'repositorySourceScreen',
                  });
                }}
              >
                <FormattedMessage {...messages.compareRepoAction} />
              </DropdownItem>
            </RepositoryAccessGuard>
            <RepositoryAccessGuard requiredLevel="write">
              <DropdownItem
                href={createFileUrl}
                onClick={e => {
                  if (frontbucketRepoSourceAddFileEnabled) {
                    e.preventDefault();
                    this.props.history.push(createFileUrl);
                  }
                }}
              >
                <FormattedMessage {...messages.newFileAction} />
              </DropdownItem>
            </RepositoryAccessGuard>
            <DropdownItem
              onClick={() => {
                publishUiEvent({
                  action: 'clicked',
                  actionSubject: 'dropdownItem',
                  actionSubjectId: 'sourceHeaderDropdownItem',
                  source: 'repositorySourceScreen',
                  attributes: {
                    item: 'manageNotifications',
                  },
                });
                this.onOpenSubscriptionsDialog();
              }}
            >
              <FormattedMessage {...messages.manageNotifications} />
            </DropdownItem>
            <DropdownItem onClick={() => this.onDownloadRepoAction(repository)}>
              <FormattedMessage {...messages.downloadRepoAction} />
            </DropdownItem>
            <LazyForgeRepoCodeOverviewActionAppsList
              onAppClick={this.onAppClick}
            />
          </DropdownItemGroup>
        </DropdownMenu>
        <LazyForgeRepoCodeOverviewActionSingleApp
          onAppClose={this.onAppClose}
          openedExtension={this.state.openedExtension}
        />
      </div>
    );

    return (
      <ButtonGroup>
        {path && pathType === 'file' && <ConnectFileActions />}
        {repositoryAccessLevel === 'admin' && (
          <CustomThemeButton onClick={() => this.shareRepo()}>
            <FormattedMessage {...messages.inviteAction} />
          </CustomThemeButton>
        )}
        {path ? <CheckoutDialogButton /> : <CloneButton />}
        {!userIsAnonymous && <HeaderDropdownMenu />}
      </ButtonGroup>
    );
  }

  render() {
    const { isDownloadRepoModalOpen } = this.state;
    const { path, repository, userIsAnonymous, location } = this.props;
    const query = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    const header = (path && path.split('/').pop()) ||
      (repository && repository.name) || (
        <FormattedMessage {...messages.heading} />
      );

    return (
      <PageHeaderWrapper layout="fluid" data-qa="page-header-wrapper">
        <PageHeaderColumn>
          <PageHeader
            actions={this.renderActions()}
            breadcrumbs={<Breadcrumbs isRoot={!path} />}
          >
            <DownloadRepositoryLfsModalDialog
              isOpen={isDownloadRepoModalOpen}
              onClose={this.closeDownloadRepoModal}
              onDownload={() => {
                this.downloadRepo();
                this.closeDownloadRepoModal();
              }}
            />
            <ModalTransition>
              {!userIsAnonymous && query.subscriptionsModalOpened ? (
                <SubscriptionsModalDialog
                  onCloseSubscriptionsDialog={this.onCloseSubscriptionsDialog}
                />
              ) : null}
            </ModalTransition>
            {header}
          </PageHeader>
        </PageHeaderColumn>
      </PageHeaderWrapper>
    );
  }
}

export const HeaderWithIntl = injectIntl(Header);
export default HeaderWithIntl;
